import { Banner, Button, Card, Select, Text, TextField } from "@shopify/polaris"
import { CaretDownIcon, CaretUpIcon } from "@shopify/polaris-icons"
import classNames from "classnames"
import { observer } from "mobx-react"
import { PSwitch } from "polaris-enhance"
import { ReactNode, useEffect, useRef, useState } from "react"
import { Trans } from "react-i18next"

import IdeaIcon from "@/assets/svg/IdeaIcon"
import stores from "@/stores"

import StepHeader from "../components/StepHeader"
import {
  categoryShortLabelMap,
  getReturnRuleTypeOptions,
  getSolutionOptions,
} from "../config"

const ConfigCard = observer(
  (props: {
    icon: string
    title: string
    description: string
    children?: ReactNode
    expanded: boolean
    onExpand: () => void
  }) => {
    const { icon, title, description, children, expanded, onExpand } = props
    const contentRef = useRef<HTMLDivElement>(null)
    const [contentHeight, setContentHeight] = useState(0)

    useEffect(() => {
      if (!contentRef.current) return
      setContentHeight(contentRef.current.scrollHeight)
    }, [children, expanded])

    return (
      <Card padding="500">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#e7f1ff] text-sm">
              {icon}
            </div>
            <div>
              <Text as="p" fontWeight="semibold" variant="bodyMd">
                {title}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {description}
              </Text>
            </div>
          </div>
          <Button
            icon={expanded ? CaretUpIcon : CaretDownIcon}
            variant="tertiary"
            onClick={onExpand}
          ></Button>
        </div>
        <div
          className={classNames(
            expanded ? "" : "overflow-hidden",
            "transition-[height,opacity] duration-300 ease-in-out",
          )}
          style={{
            height: expanded ? contentHeight : 0,
            opacity: expanded ? 1 : 0,
          }}
        >
          {children ? (
            <div ref={contentRef} className="pt-5">
              {children}
            </div>
          ) : null}
        </div>
      </Card>
    )
  },
)

const StepTwo = observer(
  (props: { onBack: () => void; onContinue: () => void }) => {
    const { onBack, onContinue } = props
    const onboarding = stores.OnboardingStore
    const [showReturnWindowBanner, setShowReturnWindowBanner] = useState(true)
    const [showDeliveryDateBanner, setShowDeliveryDateBanner] = useState(true)

    const reasons = onboarding.currentReasons
    const selectedSolutionsCount = onboarding.selectedSolutions.length

    const solutionDesc = (() => {
      if (!selectedSolutionsCount) return ""
      const refundCount = onboarding.selectedSolutions.filter((item) =>
        [1, 7, 2, 4].includes(item.value),
      ).length
      const exchangeCount = selectedSolutionsCount - refundCount
      return t("ExchangeSolution×XAndRefundSolution×Y", {
        x: exchangeCount,
        y: refundCount,
      })
    })()

    const handleContinue = async () => {
      if (!onboarding.selectedSolutions.length) return
      onboarding.saveStep2()
      onContinue()
    }

    return (
      <div id="step2" className="flex flex-col gap-5">
        <StepHeader
          description={t("BasedStoreTypeHeresWhatSetUpReviewAdj")}
          title={t("WeveConfiguredThis")}
        />
        <div className="flex flex-col gap-5">
          <ConfigCard
            description={t("ReasonsConfiguredStore", {
              x: onboarding.selectedReasons.length,
            })}
            icon="📋"
            title={t("ReturnReasons")}
            expanded={onboarding.expandedCards.has(1)}
            onExpand={() => onboarding.toggleExpandedCard(1)}
          >
            <div className="flex flex-col">
              {reasons.length === 0 ? (
                <Text as="p" tone="subdued" variant="bodySm">
                  {t("NoReasonsAvailableCurrentLocale")}
                </Text>
              ) : (
                reasons.map((reason) => {
                  const reasonId = reason.id
                  const isOn = onboarding.selectedReasons.includes(reasonId)
                  return (
                    <div
                      className="flex items-center justify-between border-0 border-b border-solid border-[var(--p-color-border-secondary)] py-2 last:border-b-0"
                      key={reasonId}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] leading-5 text-[var(--p-color-text)]">
                          {reason.value}
                        </span>
                        <span className="rounded-lg bg-[#e7f1ff] px-3 text-xs leading-5 text-[#1274ff]">
                          {categoryShortLabelMap()[reason.category]}
                        </span>
                      </div>
                      <span className="onboarding-reason-switch">
                        <PSwitch
                          onChange={() => onboarding.toggleReason(reasonId)}
                          value={isOn}
                          type="pill"
                        />
                      </span>
                    </div>
                  )
                })
              )}
            </div>
          </ConfigCard>
          <ConfigCard
            description={t("WithinXDaysOrderFulfillmentDate", {
              x: onboarding.returnDays,
            })}
            icon="📅"
            title={t("ReturnWindow")}
            expanded={onboarding.expandedCards.has(2)}
            onExpand={() => onboarding.toggleExpandedCard(2)}
          >
            <div className="flex flex-col gap-5">
              {showReturnWindowBanner ? (
                <Banner
                  icon={IdeaIcon}
                  tone="info"
                  onDismiss={() => setShowReturnWindowBanner(false)}
                >
                  <p>{t("USMerchantsTypicallyOffer30DaysEULawReq")}</p>
                </Banner>
              ) : null}
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Trans
                    components={[
                      <TextField
                        autoComplete="off"
                        label=""
                        labelHidden
                        max={365}
                        min={0}
                        onChange={(value) =>
                          onboarding.setReturnDays(parseInt(value) || 0)
                        }
                        type="number"
                        value={String(onboarding.returnDays)}
                      />,
                      <Select
                        label=""
                        labelHidden
                        onChange={(value) =>
                          onboarding.setReturnRuleType(value)
                        }
                        options={getReturnRuleTypeOptions()}
                        value={onboarding.returnRuleType}
                      />,
                    ]}
                    i18nKey="WithinDaysAfterDate"
                  />
                </div>
                {onboarding.returnRuleType === "order_delivery" &&
                showDeliveryDateBanner ? (
                  <Banner
                    tone="info"
                    onDismiss={() => setShowDeliveryDateBanner(false)}
                  >
                    <p>{t("DeliveryDateBasedShopifyOrdersDeliver")}</p>
                  </Banner>
                ) : null}
              </div>
            </div>
          </ConfigCard>
          <ConfigCard
            description={solutionDesc}
            icon="💰"
            title={t("ReturnSolutions")}
            expanded={onboarding.expandedCards.has(3)}
            onExpand={() => onboarding.toggleExpandedCard(3)}
          >
            <div className="flex flex-col gap-5">
              {onboarding.selectedSolutions.some(Boolean) ? null : (
                <Banner tone="critical">
                  <p>{t("LeastOneReturnSolutionMustEnabled")}</p>
                </Banner>
              )}
              <div className="flex flex-col">
                {getSolutionOptions().map((solution) => {
                  const isOn = onboarding.selectedSolutions.some(
                    (item) => item.value === solution.value,
                  )
                  return (
                    <div
                      className="flex items-center justify-between border-0 border-b border-solid border-[var(--p-color-border-secondary)] py-2 last:border-b-0"
                      key={solution.value}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] leading-5 text-[var(--p-color-text)]">
                          {solution.label}
                        </span>
                      </div>
                      <span className="onboarding-reason-switch">
                        <PSwitch
                          onChange={() =>
                            onboarding.toggleSolution(solution.value)
                          }
                          value={isOn}
                        />
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </ConfigCard>
        </div>
        <div className="flex items-center justify-between">
          <Button
            disabled={onboarding.isSaving}
            onClick={onBack}
            variant="tertiary"
          >
            {t("Back_0")}
          </Button>
          <div className="blueButton">
            <Button
              disabled={
                onboarding.isSaving || !onboarding.selectedSolutions.length
              }
              loading={onboarding.isSaving}
              onClick={handleContinue}
              variant="tertiary"
            >
              {t("LooksGoodContinue")}
            </Button>
          </div>
        </div>
      </div>
    )
  },
)

export default StepTwo
